import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { getCachedOnboardingStatus, setCachedOnboardingStatus } from '@/lib/onboardingCache';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

/**
 * OnboardingGuard - Optimized with user_metadata and localStorage cache
 * 
 * Strategy:
 * 1. Check user_metadata first (instant, no DB query)
 * 2. Check localStorage cache (fast fallback)
 * 3. Query database only if both fail (rare case)
 * 
 * Result: Zero loading screen for users who completed onboarding
 */
export const OnboardingGuard = ({ children }: OnboardingGuardProps) => {
  const [isChecking, setIsChecking] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkOnboardingStatus();
  }, [location.pathname]);

  const checkOnboardingStatus = async () => {
    try {
      // Public routes - skip check entirely
      const publicRoutes = [
        '/onboarding',
        '/login',
        '/signup',
        '/',
        '/entrar',
        '/cadastro',
        '/auth/confirm',
        '/recuperar-senha',
        '/redefinir-senha',
      ];
      
      if (publicRoutes.includes(location.pathname)) {
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return;
      }

      // STRATEGY 1: Check user_metadata (instant, no DB query)
      const onboardingCompleted = user.user_metadata?.onboarding_completed;
      
      if (onboardingCompleted === true) {
        // Metadata says completed, but we still need to verify tenant_id exists
        // Use cache with tenant validation
        const cachedStatus = getCachedOnboardingStatus(user.id);
        
        if (cachedStatus === true) {
          // Both metadata and cache confirm - safe to proceed
          return;
        }
        
        // Metadata says yes, but cache is invalid - verify with DB
        // This handles edge cases like tenant deletion
      }

      // STRATEGY 2: Check localStorage cache (fast fallback)
      const cachedStatus = getCachedOnboardingStatus(user.id);
      
      if (cachedStatus === true) {
        // Cache hit - skip DB query
        return;
      }

      // If we reach here, neither metadata nor cache confirms completion.
      // Even if the DB trigger handle_new_user auto-created a tenant,
      // we STILL want them to go through the UI onboarding to capture
      // referral codes and properly configure their business details.
      
      setIsChecking(true);

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('tenant_id, role')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('[OnboardingGuard] Error fetching profile:', error);
      }

      // Check if onboarding is complete (profile exists, has tenant_id, AND has role)
      if (!profile || !profile.tenant_id || !profile.role) {
        // Needs onboarding
        navigate('/onboarding', { replace: true });
        return;
      }

      // Check if user is owner of the tenant
      const { data: membership } = await supabase
        .from('tenant_members')
        .select('role')
        .eq('user_id', user.id)
        .eq('tenant_id', profile.tenant_id)
        .maybeSingle();

      const isOwner = membership?.role === 'owner';
      const hasMembership = !!membership;

      // If user has no membership, onboarding is NOT complete
      if (!hasMembership) {
        navigate('/onboarding', { replace: true });
        return;
      }

      // Onboarding complete - update metadata and cache for future logins
      try {
        await updateOnboardingMetadata(user.id, true);
        // Only cache if metadata update succeeded
        setCachedOnboardingStatus(user.id, true, profile.tenant_id);
      } catch (error) {
        console.error('[OnboardingGuard] Error updating metadata:', error);
        // Still cache even if metadata update fails (graceful degradation)
        setCachedOnboardingStatus(user.id, true, profile.tenant_id);
      }
      
      setIsChecking(false);
    } catch (error) {
      console.error('[OnboardingGuard] Error checking onboarding:', error);
      setIsChecking(false);
    }
  };

  /**
   * Update user_metadata with onboarding status
   * This ensures future logins are instant (no DB query needed)
   */
  const updateOnboardingMetadata = async (userId: string, completed: boolean) => {
    try {
      await supabase.auth.updateUser({
        data: { onboarding_completed: completed }
      });
    } catch (error) {
      console.error('[OnboardingGuard] Error updating metadata:', error);
    }
  };

  // Only show loading if we're actually checking the database
  // (which should be rare after first login)
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 relative">
            <div className="absolute inset-0 rounded-full border-4 border-indigo-200 dark:border-indigo-900"></div>
            <div className="absolute inset-0 rounded-full border-4 border-indigo-600 dark:border-indigo-400 border-t-transparent animate-spin"></div>
          </div>
          <p className="text-slate-600 dark:text-slate-400 font-medium">Verificando conta...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
